import { ComponentType, ReactNode } from "react";
import type { LucideProps } from "lucide-react";
import { cn } from "@/lib/utils";

export type GlowAccent = "blue" | "violet" | "emerald" | "amber";

const ACCENT_GRADIENTS: Record<GlowAccent, string> = {
  blue: "from-primary to-primary-light",
  violet: "from-[hsl(265_85%_60%)] to-[hsl(280_85%_70%)]",
  emerald: "from-[hsl(160_75%_42%)] to-[hsl(150_75%_55%)]",
  amber: "from-[hsl(35_95%_55%)] to-[hsl(45_95%_65%)]",
};

const ACCENT_GLOW: Record<GlowAccent, string> = {
  blue: "hsl(var(--primary) / 0.25)",
  violet: "hsl(265 85% 60% / 0.22)",
  emerald: "hsl(160 75% 42% / 0.22)",
  amber: "hsl(35 95% 55% / 0.22)",
};

interface GlowCardProps {
  icon?: ComponentType<LucideProps>;
  accent?: GlowAccent;
  title?: ReactNode;
  description?: ReactNode;
  /** Disable hover lift */
  static?: boolean;
  className?: string;
  children?: ReactNode;
}

export const GlowCard = ({
  icon: Icon,
  accent = "blue",
  title,
  description,
  static: isStatic = false,
  className,
  children,
}: GlowCardProps) => {
  return (
    <div
      className={cn(
        "relative bg-card border border-border/60 rounded-2xl p-5 md:p-6 shadow-card overflow-hidden",
        !isStatic && "hover:-translate-y-1 hover:shadow-stat transition-all duration-300",
        className
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-70 blur-3xl"
        style={{ background: `radial-gradient(circle, ${ACCENT_GLOW[accent]}, transparent 70%)` }}
      />
      {Icon && (
        <div
          className={cn(
            "relative w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center mb-4 text-white shadow-lg shadow-primary/20",
            ACCENT_GRADIENTS[accent]
          )}
        >
          <Icon className="w-6 h-6" />
        </div>
      )}
      {title && (
        <h3 className="relative text-lg md:text-xl font-bold text-foreground mb-2 tracking-tight">
          {title}
        </h3>
      )}
      {description && (
        <p className="relative text-sm md:text-base text-muted-foreground leading-relaxed">
          {description}
        </p>
      )}
      {children && <div className="relative">{children}</div>}
    </div>
  );
};

export default GlowCard;
